import { Observable, race, timer } from 'rxjs';
import { filter, take } from 'rxjs/operators';

export interface TierSchedule {
  /** Fired immediately - critical, above-the-fold data (e.g. summary, sidebar). */
  tier1: Array<() => void>;
  /** Fired immediately alongside tier1 - near-critical data (e.g. attendance count). */
  tier2: Array<() => void>;
  /** Fired once tier1Ready$ emits, or after tier3DelayMs, whichever comes first. */
  tier3: Array<() => void>;
  /** Observable that emits truthy once tier1's critical data has landed (cache or network). */
  tier1Ready$?: Observable<any>;
  /** Max wait before firing tier3 even if tier1Ready$ never emits. Defaults to 500ms. */
  tier3DelayMs?: number;
}

/**
 * Fires tier1/tier2 loaders immediately, and defers tier3 loaders just long enough
 * that they don't compete with tier1/tier2 for a browser connection slot - without
 * ever blocking tier3 indefinitely.
 */
export function runTiered(schedule: TierSchedule): void {
  schedule.tier1.forEach(fn => fn());
  schedule.tier2.forEach(fn => fn());

  const delay$ = timer(schedule.tier3DelayMs ?? 500);
  const ready$ = schedule.tier1Ready$
    ? race(delay$, schedule.tier1Ready$.pipe(filter(v => !!v)))
    : delay$;

  ready$.pipe(take(1)).subscribe(() => {
    schedule.tier3.forEach(fn => fn());
  });
}
